import { motion } from 'framer-motion';
import '../styles/how-it-works-animations.css';

// Animated Illustration 1: Register Form (6s loop)
function RegisterIllustration() {
  return (
    <div className="illustration-grid h-full w-full p-4 flex flex-col justify-center items-center gap-3 relative overflow-hidden register-animation">
      {/* Input field 1 - Agent Name */}
      <div className="w-full max-w-[180px] input-field-1">
        <div className="text-[9px] text-zinc-600 mb-1">Agent Name</div>
        <div className="h-7 rounded border border-zinc-800 bg-zinc-900/50 px-2 flex items-center overflow-hidden">
          <span className="agent-name-text text-[10px] text-zinc-400 whitespace-nowrap">my-trading-agent</span>
          <span className="typing-cursor">|</span>
        </div>
      </div>

      {/* Input field 2 - Autonomy Level */}
      <div className="w-full max-w-[180px] input-field-2">
        <div className="text-[9px] text-zinc-600 mb-1">Autonomy Level</div>
        <div className="h-7 rounded border border-zinc-800 bg-zinc-900/50 px-2 flex items-center justify-between">
          <span className="level-text text-[10px] text-zinc-500">autonomous</span>
          <span className="text-[8px] text-zinc-600">▼</span>
        </div>
      </div>

      {/* Button */}
      <div className="w-full max-w-[180px] mt-1 create-button">
        <div className="btn-default h-8 rounded border border-zinc-700 bg-zinc-900/30 flex items-center justify-center gap-2">
          <span className="text-[10px] text-zinc-300">Create Agent</span>
          <span className="text-[9px] text-zinc-500">→</span>
        </div>
        <div className="btn-success h-8 rounded border border-zinc-600 bg-zinc-800/50 flex items-center justify-center gap-2 absolute inset-0">
          <span className="text-[10px] text-zinc-200">✓ Agent Created</span>
        </div>
      </div>

      {/* Confirmation card */}
      <div className="confirmation-card w-full max-w-[180px] mt-2 p-2 rounded border border-zinc-700/50 bg-zinc-900/60">
        <div className="text-[9px] text-zinc-300 font-medium">my-trading-agent</div>
        <div className="text-[8px] text-zinc-500">autonomous · Active</div>
      </div>

      {/* Cursor */}
      <div className="cursor-icon absolute text-[12px] text-zinc-500">↗</div>
    </div>
  );
}

// Animated Illustration 2: Chain Network (7s loop)
function ChainsIllustration() {
  const chains = [
    { name: 'ETH', angle: -90, delay: 0.5 },
    { name: 'SOL', angle: -18, delay: 1.2 },
    { name: 'BASE', angle: 54, delay: 1.9 },
    { name: 'ARB', angle: 126, delay: 2.6 },
    { name: 'OP', angle: 198, delay: 3.3 },
  ];

  return (
    <div className="illustration-grid h-full w-full relative flex items-center justify-center chains-animation overflow-hidden">
      {/* SVG for connection lines */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200">
        {chains.map((chain, i) => {
          const rad = (chain.angle * Math.PI) / 180;
          const x2 = 100 + 60 * Math.cos(rad);
          const y2 = 100 + 60 * Math.sin(rad);
          return (
            <line
              key={i}
              className={`connection-line line-${i}`}
              x1="100" y1="100"
              x2={x2} y2={y2}
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="1"
              style={{ animationDelay: `${chain.delay}s` }}
            />
          );
        })}
      </svg>

      {/* Concentric rings */}
      <div className="absolute w-[100px] h-[100px] rounded-full border border-zinc-800/40 ring-pulse" />
      <div className="absolute w-[140px] h-[140px] rounded-full border border-zinc-800/25" />

      {/* Center AIBL node */}
      <div className="center-node relative w-9 h-9 rounded-lg border-2 border-zinc-500 flex items-center justify-center z-10 bg-zinc-900/80">
        <span className="text-[7px] font-bold text-zinc-300">AIBL</span>
      </div>

      {/* Chain nodes */}
      {chains.map((chain, i) => {
        const rad = (chain.angle * Math.PI) / 180;
        const x = 50 + 30 * Math.cos(rad);
        const y = 50 + 30 * Math.sin(rad);

        return (
          <div
            key={i}
            className={`chain-node node-${i} absolute w-7 h-7 rounded-full flex items-center justify-center`}
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: 'translate(-50%, -50%)',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              animationDelay: `${chain.delay}s`,
            }}
          >
            <span className="text-[7px] font-medium text-zinc-400">{chain.name}</span>
          </div>
        );
      })}

      {/* Data packets */}
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`data-packet packet-${i} absolute w-[3px] h-[3px] rounded-full bg-white/60`}
        />
      ))}
    </div>
  );
}

// Animated Illustration 3: Transaction Flow (8s loop)
function TransactIllustration() {
  return (
    <div className="illustration-grid h-full w-full p-3 flex flex-col relative overflow-hidden transact-animation">
      {/* Timeline line */}
      <div className="absolute left-4 top-3 bottom-3 w-px border-l border-dashed border-zinc-800/60" />

      {/* Transaction 1 */}
      <div className="tx-card tx-1 relative rounded-lg border border-zinc-800/60 p-2.5 ml-3 mb-2 bg-zinc-900/40">
        <div className="flex items-start gap-2">
          <span className="text-[9px] text-zinc-600">↗</span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-medium text-zinc-400">Purchase AI Credits</div>
            <div className="text-[8px] text-zinc-600">$50 · Claude API</div>
            <div className="text-[7px] text-zinc-700 mt-0.5">Paid from: Robinhood ETH</div>
          </div>
          <div className="status-area relative">
            <span className="status-pending text-[8px] text-zinc-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" />
            </span>
            <span className="status-confirmed text-[8px] text-zinc-400 absolute inset-0">✓</span>
          </div>
        </div>
      </div>

      {/* Transaction 2 */}
      <div className="tx-card tx-2 relative rounded-lg border border-zinc-800/60 p-2.5 ml-3 mb-2 bg-zinc-900/40">
        <div className="flex items-start gap-2">
          <span className="text-[9px] text-zinc-600">↗</span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-medium text-zinc-400">Swap Tokens</div>
            <div className="text-[8px] text-zinc-600">0.5 ETH → 1,200 USDC</div>
          </div>
          <div className="status-area relative">
            <span className="status-pending text-[8px] text-zinc-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" />
            </span>
            <span className="status-confirmed text-[8px] text-zinc-400 absolute inset-0">✓</span>
          </div>
        </div>
      </div>

      {/* Transaction 3 */}
      <div className="tx-card tx-3 relative rounded-lg border border-zinc-800/60 p-2.5 ml-3 mb-2 bg-zinc-900/40">
        <div className="flex items-start gap-2">
          <span className="text-[9px] text-zinc-600">↗</span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-medium text-zinc-400">Gift Card Purchase</div>
            <div className="text-[8px] text-zinc-600">$25 · Amazon</div>
          </div>
          <div className="status-area relative">
            <span className="status-pending text-[8px] text-zinc-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-pulse" />
            </span>
            <span className="status-confirmed text-[8px] text-zinc-400 absolute inset-0">✓</span>
          </div>
        </div>
      </div>

      {/* Transaction 4 (partial) */}
      <div className="tx-card tx-4 relative rounded-lg border border-zinc-800/60 p-2.5 ml-3 bg-zinc-900/40 opacity-50">
        <div className="flex items-start gap-2">
          <span className="text-[9px] text-zinc-600">↗</span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-medium text-zinc-400">Pay for Compute</div>
            <div className="text-[8px] text-zinc-600">$15 · GPU hours</div>
          </div>
        </div>
      </div>

      {/* Counter */}
      <div className="tx-counter absolute top-2 right-2 text-[8px] text-zinc-500 bg-zinc-900/80 px-2 py-1 rounded">
        4 transactions · $126.50
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-zinc-950 to-transparent pointer-events-none" />
    </div>
  );
}

// Card component
function StepCard({ step, index, illustration: Illustration }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
      className="relative rounded-2xl p-7"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}
      data-testid={`how-it-works-card-${index + 1}`}
    >
      {/* Corner dots */}
      <div className="absolute w-[5px] h-[5px] rounded-full bg-white/15 top-2 left-2" />
      <div className="absolute w-[5px] h-[5px] rounded-full bg-white/15 top-2 right-2" />
      <div className="absolute w-[5px] h-[5px] rounded-full bg-white/15 bottom-2 left-2" />
      <div className="absolute w-[5px] h-[5px] rounded-full bg-white/15 bottom-2 right-2" />

      {/* Step number */}
      <div className="flex items-center gap-2 mb-4">
        <span className="w-1 h-1 rounded-full bg-zinc-500" />
        <span className="text-[14px] font-medium text-zinc-600">{step.number}</span>
      </div>

      {/* Illustration area */}
      <div
        className="relative h-[200px] md:h-[220px] rounded-xl overflow-hidden mb-6"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <Illustration />
      </div>

      {/* Title */}
      <h3 className="text-[18px] font-semibold text-white mb-2">{step.title}</h3>

      {/* Description */}
      <p className="text-[14px] text-zinc-500 leading-[1.6]">{step.description}</p>
    </motion.div>
  );
}

const steps = [
  {
    number: '0.1',
    title: 'Register your agent',
    description: 'Create an account via API or dashboard. Each agent gets a unique identity and keys.',
  },
  {
    number: '0.2',
    title: 'Connect chains',
    description: 'Activate any chain as a plugin. Each one spins up a wallet with full DeFi access.',
  },
  {
    number: '0.3',
    title: 'Access liquidity',
    description: 'Swap tokens. Bridge cross-chain. Lend and borrow. Tap into billions in on-chain liquidity.',
  },
];

const illustrations = [RegisterIllustration, ChainsIllustration, TransactIllustration];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="py-[120px] bg-black"
      data-testid="how-it-works-section"
    >
      <div className="max-w-[1080px] mx-auto px-6">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-6"
        >
          <span
            className="text-[12px] uppercase tracking-[0.06em] text-zinc-400 px-4 py-1.5 rounded-full"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
          >
            How It Works
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-[32px] md:text-[36px] font-bold tracking-[-0.02em] text-white text-center mb-16"
          data-testid="how-it-works-title"
        >
          Three steps to on-chain liquidity.
        </motion.h2>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <StepCard
              key={index}
              step={step}
              index={index}
              illustration={illustrations[index]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
